<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import PopOverSearch from './search/PopOverSearch.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

export default {
  components: {
    ChatList,
    ConversationBox,
    PopOverSearch,
    CmdBarConversationSnooze,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
    };
  },
  data() {
    return {
      showSearchModal: false,
      mobileActiveView: 'list', // 'list' | 'conversation' | 'contact'
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
      myTeams: 'teams/getMyTeams',
    }),
    showConversationList() {
      // Always show conversation list when no conversation is selected
      if (!this.conversationId) {
        return true;
      }
      const { show_conversation_list: showConversationList = true } = this.uiSettings;
      return showConversationList;
    },
    showMessageView() {
      return true;
    },
    isContactPanelOpen() {
      if (this.currentChat.id) {
        const { is_contact_sidebar_open: isContactSidebarOpen } =
          this.uiSettings;
        return isContactSidebarOpen;
      }
      return false;
    },
    showPopOverSearch() {
      return !this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.CHATWOOT_V4
      );
    },
    isMobile() {
      const { SMALL_SCREEN_BREAKPOINT } = wootConstants;
      return window.innerWidth <= SMALL_SCREEN_BREAKPOINT;
    },
    // Mobile view for phones (768px breakpoint)
    isMobileView() {
      return window.innerWidth <= 768;
    },
    hideAllChatsRestrictionEnabled() {
      if (this.currentRole === 'administrator') {
        return false;
      }

      const accountId =
        typeof this.accountId === 'object' && this.accountId?.value !== undefined
          ? this.accountId.value
          : Number(this.accountId || this.$route.params.accountId);

      return this.isFeatureEnabledonAccount(
        accountId,
        'hide_all_chats_for_agent'
      );
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
      this.handleMobileContactPanelOnConversationChange();
      // Switch to conversation view on mobile when conversation is selected
      if (this.isMobileView && this.conversationId) {
        this.mobileActiveView = 'conversation';
      }
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
    // Handle mobile contact panel on initial mount
    this.handleMobileContactPanelOnConversationChange();
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    async fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }

      const chat = this.findConversation();
      if (chat) {
        return;
      }

      const result = await this.$store.dispatch(
        'getConversation',
        this.conversationId
      );

      if (result?.reason === 'unauthorized') {
        this.handleUnauthorizedConversationAccess();
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      if (!chat) {
        return undefined;
      }

      if (
        this.hideAllChatsRestrictionEnabled &&
        !this.isConversationOwnedByCurrentAgent(chat)
      ) {
        return undefined;
      }

      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }

        if (
          this.hideAllChatsRestrictionEnabled &&
          !this.isConversationOwnedByCurrentAgent(selectedConversation)
        ) {
          this.handleUnauthorizedConversationAccess();
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    isConversationOwnedByCurrentAgent(conversation) {
      if (!conversation) {
        return false;
      }

      const assigneeId = conversation?.meta?.assignee?.id;
      const currentUserId = this.currentUser?.id;

      // If assigned directly to current user, always allowed
      if (assigneeId === currentUserId) {
        return true;
      }

      // manager concept: Managers can also access conversations in their teams
      if (this.currentRole === 'manager') {
        const conversationTeamId = conversation?.meta?.team?.id;
        if (conversationTeamId) {
          const myTeamIds = (this.myTeams || []).map(team => team.id);
          if (myTeamIds.includes(conversationTeamId)) {
            return true;
          }
        }
      }

      return false;
    },
    handleUnauthorizedConversationAccess() {
      this.$store.dispatch('clearSelectedState');

      const accountId = this.$route.params.accountId;
      if (this.$route.name !== 'home') {
        this.$router.replace({
          name: 'home',
          params: { accountId },
        });
      }
    },
    onToggleContactPanel() {
      // On mobile, navigate to contact view instead of toggling sidebar
      if (this.isMobileView) {
        this.mobileActiveView = 'contact';
        return;
      }
      this.updateUISettings({
        is_contact_sidebar_open: !this.isContactPanelOpen,
      });
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
    handleMobileContactPanelOnConversationChange() {
      // Close contact panel on mobile when conversation opens
      if (this.isMobile && this.conversationId && this.isContactPanelOpen) {
        this.updateUISettings({
          is_contact_sidebar_open: false,
        });
      }
    },
    toggleConversationLayout() {
      // Don't allow hiding chat list when no conversation is selected
      if (!this.conversationId) {
        return;
      }
      const { show_conversation_list: showConversationList = true } = this.uiSettings;
      this.updateUISettings({
        show_conversation_list: !showConversationList,
      });
    },
    // Mobile navigation methods
    navigateToConversation() {
      this.mobileActiveView = 'conversation';
    },
    navigateToContact() {
      this.mobileActiveView = 'contact';
    },
    navigateBackToList() {
      this.mobileActiveView = 'list';
    },
    navigateBackToConversation() {
      this.mobileActiveView = 'conversation';
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full">
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :has-selected-conversation="!!conversationId"
      :is-mobile-active="!isMobileView || mobileActiveView === 'list'"
      @conversation-load="onConversationLoad"
    >
      <template #default="{ hasAppliedFiltersOrActiveFolders, isSelectionMode }">
        <PopOverSearch
          v-if="showPopOverSearch"
          :hasAppliedFiltersOrActiveFolders="hasAppliedFiltersOrActiveFolders"
          :isSelectionMode="isSelectionMode"
          @toggle-conversation-layout="toggleConversationLayout"
        />
      </template>
    </ChatList>
    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-contact-panel-open="isContactPanelOpen"
      :show-chat-list="showConversationList"
      :is-mobile="isMobileView"
      :mobile-active-view="mobileActiveView"
      @contact-panel-toggle="onToggleContactPanel"
      @toggle-chat-list="toggleConversationLayout"
      @mobile-back-to-list="navigateBackToList"
      @mobile-navigate-to-contact="navigateToContact"
      @mobile-back-to-conversation="navigateBackToConversation"
    />
    <CmdBarConversationSnooze />
  </section>
</template>
